import { EmptyState } from '@/components/EmptyState';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlarmClock,
  AlarmClockOff,
  Repeat,
  Archive,
  ArchiveRestore,
  ArrowRight,
  FileIcon,
  KeyRound,
  Link2,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Tag,
  Unlink,
  UserMinus,
  UserPlus,
  Hourglass,
  CircleCheck,
  CornerDownRight,
  Shapes,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import type { TaskAttachment, TaskDetail, TaskEvent, TaskLabel, TaskMember } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { confirmDelete } from '@/components/confirm-lifecycle';
import { deleteTaskChat, editTaskChat } from '@/lib/tasks-api';
import { dismissEvent, optimisticUpdate, usePendingEvent } from '@/stores/taskOptimistic';
import { cn } from '@/lib/utils';
import { formatBytes, formatDateTime } from '@/lib/formatters';
import { snoozeDay } from '@/lib/snooze';
import { describeTaskRepeat, type TaskRepeat } from '@shared/task-repeat';
import { formatTaskNumber } from '@shared/task-number';
import { useAuthStore } from '@/stores/authStore';
import { usePref } from '@/hooks/use-pref';
import { UI_PREFS } from '@/lib/ui-prefs';
import { MentionText } from './MentionText';
import { factoryMessageKind, roundsByMessage, textAroundBlock } from '@shared/factory-rounds';
import { FactoryQuestionsPointer } from './FactoryQuestionsCard';
import { FactorySuggestionsPointer } from './FactorySuggestionsCard';
import { decisionRoundId, showFoldingCard } from './folding-card';
import { MentionTextarea } from './MentionTextarea';
import { displayToMarkup, markupToDisplay, type PickedMention } from './mention-text';
import { formatRelative, memberHandle, memberName } from './task-ui';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { TaskChatComposer } from './TaskChatComposer';
import { isPlayableVideo } from './task-files';
import { useAttachmentLink } from './task-attachment-link';
import { Spinner } from '@/components/ui/spinner';

/** Who or what a Waiting on line names: the name saved with it, else the member. */
function waitName(p: Record<string, any>, person: (id: unknown) => string): string {
  if (p.name) return String(p.name);
  if (p.waiting?.target === 'user') return person(p.waiting.user_id);
  return p.waiting?.target === 'ai' ? 'the AI builder' : 'another task';
}

const FIELD_NAMES: Record<string, string> = { size: 'size', kind: 'kind', prs: 'pull requests' };

function fieldLine(field: string, from: unknown, to: unknown): string {
  const name = FIELD_NAMES[field] ?? field;
  if (field === 'prs') {
    const before = Array.isArray(from) ? from.length : 0;
    const after = Array.isArray(to) ? to.length : 0;
    if (after > before) return after - before === 1 ? 'added a pull request' : `added ${after - before} pull requests`;
    if (after < before) return before - after === 1 ? 'removed a pull request' : `removed ${before - after} pull requests`;
    return 'updated a pull request’s status';
  }
  if (to === null || to === undefined) return `cleared the ${name}`;
  return from ? `changed the ${name} from ${String(from)} to ${String(to)}` : `set the ${name} to ${String(to)}`;
}

/** Lines no one did: a snooze ending, a Repeat coming round, Jev's suggestion, a task waited on finishing. */
function hasNoActor(event: TaskEvent): boolean {
  const p = event.payload;
  if (event.kind === 'unsnoozed') return p.reason === 'time';
  if (event.kind === 'repeated' || p.repeated_from) return true;
  if (event.kind === 'type_set') return Boolean(p.suggested);
  if (event.kind === 'waiting_removed') return p.reason === 'done' || p.reason === 'finished';
  return false;
}

function systemLine(event: TaskEvent, members: TaskMember[], labels: TaskLabel[]): string {
  const p = event.payload as Record<string, any>;
  const person = (id: unknown) => memberHandle(members.find((m) => m.user_id === id));
  switch (event.kind) {
    case 'created':
      // Made by a Repeat (T-49): no one did it.
      if (p.repeated_from) return `Made by the Repeat on ${formatTaskNumber(p.repeated_from.number)}${p.column_name ? `, in ${p.column_name}` : ''}`;
      return `created this task${p.column_name ? ` in ${p.column_name}` : ''}`;
    case 'moved':
      return `moved this from ${p.from ?? 'another column'} to ${p.to}`;
    case 'renamed':
      return `renamed this from “${p.from}”`;
    case 'described':
      return 'updated the description';
    case 'assigned':
      return event.actor_user_id === p.user_id ? 'joined this task' : `assigned ${person(p.user_id)}`;
    case 'unassigned':
      return event.actor_user_id === p.user_id ? 'left this task' : `removed ${person(p.user_id)}`;
    case 'context_added':
      return `linked ${p.context?.title ?? 'something'}`;
    case 'context_removed':
      return `unlinked ${p.context?.title ?? 'something'}`;
    case 'label_added':
      return `added the label ${labels.find((l) => l.id === p.label_id)?.name ?? p.name}`;
    case 'label_removed':
      return `removed the label ${labels.find((l) => l.id === p.label_id)?.name ?? p.name}`;
    case 'archived':
      return 'archived this task';
    case 'unarchived':
      return 'put this task back on the Board';
    case 'snoozed':
      return typeof p.until === 'string' ? `snoozed this until ${snoozeDay(p.until)}` : 'snoozed this';
    case 'unsnoozed':
      // `time`: no one did it, the snooze ended (see SystemLine).
      return p.reason === 'time' ? 'Snooze ended: back on the Board' : p.reason === 'moved' ? 'ended the snooze by moving it' : 'unsnoozed this';
    case 'repeat_set': {
      const text = p.repeat ? describeTaskRepeat(p.repeat as TaskRepeat) : null;
      if (!text) return 'stopped this repeating';
      return `set this to repeat ${text.charAt(0).toLowerCase()}${text.slice(1)}${p.repeat.new_task ? ', as a new task each time' : ''}`;
    }
    case 'repeated':
      // No one did it: the Repeat came round (see SystemLine).
      if (p.new_task) return `Came round again as ${formatTaskNumber(p.new_task.number)}, which carries the Repeat on`;
      return p.reopened ? `Came round again: back in ${p.to ?? 'the first column'}` : 'Came round again (still open)';
    // T-331: type, fields and Waiting on.
    case 'type_set':
      // Jev's suggestion has no actor (see SystemLine).
      if (p.suggested) return `Jev suggested the type ${p.to_name ?? 'for this task'}${p.kind ? ` (${p.kind})` : ''}`;
      return `changed the type${p.from_name ? ` from ${p.from_name}` : ''} to ${p.to_name ?? 'another type'}`;
    case 'field_set':
      return fieldLine(p.field, p.from, p.to);
    case 'waiting_added':
      return `set this waiting on ${waitName(p, person)}`;
    case 'waiting_removed':
      if (p.reason === 'replied') return `replied, so it’s no longer waiting on ${p.waiting?.target === 'user' && p.waiting.user_id === event.actor_user_id ? 'them' : waitName(p, person)}`;
      if (p.reason === 'done') return `${waitName(p, person)} is done, so it’s no longer waiting on it`;
      if (p.reason === 'finished') return `No longer waiting on ${waitName(p, person)}: this task is done`;
      if (p.reason === 'review') return `moved this to Review, so it’s no longer waiting on ${waitName(p, person)}`;
      return `stopped waiting on ${waitName(p, person)}`;
    default:
      return 'updated this task';
  }
}

function FileLink({ a }: { a: TaskAttachment }) {
  const link = useAttachmentLink(a);
  return (
    <a
      {...link}
      className="flex w-fit max-w-full items-center gap-2 rounded-md border px-2 py-1.5 text-xs hover:bg-muted"
    >
      <FileIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{a.filename}</span>
      <span className="shrink-0 text-muted-foreground">{formatBytes(a.size)}</span>
    </a>
  );
}

/** An image preview; if it can't load (an expired link, say) it falls back to a file link. */
function ImagePreview({ a }: { a: TaskAttachment }) {
  const [failed, setFailed] = useState(false);
  const link = useAttachmentLink(a);
  if (failed) return <FileLink a={a} />;
  return (
    <a {...link} data-hint={a.filename}>
      <img
        src={a.url}
        alt={a.filename}
        className="max-h-40 max-w-full rounded-md border object-contain sm:max-w-[16rem]"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </a>
  );
}

/** An MP4 that plays in place; like an image, it falls back to a file link if it can't load. */
function VideoPreview({ a }: { a: TaskAttachment }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <FileLink a={a} />;
  return (
    <video
      src={a.url}
      controls
      playsInline
      preload="metadata"
      title={a.filename}
      className="block max-h-72 w-full max-w-md rounded-md border bg-black"
      onError={() => setFailed(true)}
    />
  );
}

export function AttachmentList({ attachments }: { attachments: TaskAttachment[] }) {
  if (!attachments.length) return null;
  const images = attachments.filter((a) => a.is_image);
  const videos = attachments.filter((a) => isPlayableVideo(a));
  const files = attachments.filter((a) => !a.is_image && !isPlayableVideo(a));
  return (
    <div className="mt-2 space-y-2">
      {images.length ? (
        <div className="flex flex-wrap gap-2">
          {images.map((a) => (
            <ImagePreview key={a.id} a={a} />
          ))}
        </div>
      ) : null}
      {videos.map((a) => (
        <VideoPreview key={a.id} a={a} />
      ))}
      {files.map((a) => (
        <FileLink key={a.id} a={a} />
      ))}
    </div>
  );
}

/** Which round of the factory's questions or suggestions a comment belongs to (T-55). */
type MessageRound = { roundId: string; settled: boolean };

/**
 * T-55: a comment that asks the factory's questions or makes its suggestions shows what's said around
 * the block and a line pointing to its card in the Decisions; one that answers or decides them, a
 * line saying so (with a reply in words' text under it).
 */
function FactoryMessageBody({
  body,
  round,
  members,
  contexts,
  onNavigate,
}: {
  body: string;
  round: MessageRound | undefined;
  members: TaskMember[];
  contexts: TaskDetail['contexts'];
  onNavigate?: () => void;
}) {
  const kind = factoryMessageKind(body);
  const show = () => {
    if (round) showFoldingCard(decisionRoundId(round.roundId));
  };
  const text = (t: string) => (t ? <MentionText text={t} members={members} contexts={contexts} onNavigate={onNavigate} className="mt-0.5 text-sm leading-relaxed" /> : null);
  if (!kind || !round) return text(body);
  if (kind.kind === 'questions' || kind.kind === 'suggestions') {
    return (
      <div className="space-y-2">
        {text(textAroundBlock(body))}
        <div className="mt-1">
          {kind.kind === 'questions' ? (
            <FactoryQuestionsPointer count={kind.count} answered={round.settled} onShow={show} />
          ) : (
            <FactorySuggestionsPointer count={kind.count} decided={round.settled} onShow={show} />
          )}
        </div>
      </div>
    );
  }
  const line = kind.kind === 'decisions' ? (kind.count === 1 ? 'Decided a suggestion' : `Decided ${kind.count} suggestions`) : kind.kind === 'reply' ? 'Replied to the questions' : 'Answered the questions';
  return (
    <div className="space-y-1">
      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
        <CornerDownRight className="size-3.5 shrink-0" aria-hidden />
        <span>{line}</span>
        <Button variant="link" size="inline" onClick={show}>
          See Decisions
        </Button>
      </p>
      {kind.kind === 'reply' ? text(kind.text) : null}
    </div>
  );
}

function ChatMessage({
  task,
  event,
  members,
  round,
  onNavigate,
}: {
  task: TaskDetail;
  event: TaskEvent;
  members: TaskMember[];
  /** The factory's round this comment asks or answers, if any (T-55). */
  round?: MessageRound;
  onNavigate?: () => void;
}) {
  const me = useAuthStore((s) => s.user?.id);
  const [editing, setEditing] = useState<{ text: string; mentions: PickedMention[] } | null>(null);
  const pending = usePendingEvent(task.id, event.id);
  const author = members.find((m) => m.user_id === event.actor_user_id);
  const mine = event.actor_user_id === me && !pending;

  const save = () => {
    if (!editing) return;
    const body = displayToMarkup(editing.text, editing.mentions);
    setEditing(null);
    const editedAt = new Date().toISOString();
    void optimisticUpdate(
      task.id,
      { detail: (t) => ({ ...t, events: t.events.map((e) => (e.id === event.id ? { ...e, body, edited_at: editedAt } : e)) }) },
      editTaskChat(task.id, event.id, body)
    );
  };

  const remove = async () => {
    const ok = await confirmDelete({ what: 'message', name: 'this message', consequence: 'Its files are deleted too.' });
    if (!ok) return;
    void optimisticUpdate(
      task.id,
      {
        // Deleted messages leave the timeline altogether, and the card's counts.
        detail: (t) => ({ ...t, events: t.events.filter((e) => e.id !== event.id) }),
        counts: { chat: -1, attachments: -event.attachments.length },
      },
      deleteTaskChat(task.id, event.id)
    );
  };

  return (
    <div className="group relative flex gap-3">
      <MemberAvatar name={memberName(author)} photourl={author?.photourl} size="sm" ring className="z-10 mt-1" />
      <div
        className={cn(
          'min-w-0 flex-1 rounded-lg border bg-card px-3 py-2 shadow-xs transition-opacity',
          pending?.status === 'sending' && 'opacity-70',
          pending?.status === 'failed' && 'border-destructive/50'
        )}
      >
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-foreground" data-hint={memberName(author)}>
            {memberHandle(author)}
          </span>
          <ViaApiKey name={event.via_api_key_name} />
          <span className="text-muted-foreground" data-hint={formatDateTime(new Date(event.created_at))}>
            {pending?.status === 'sending' ? (
              <span className="inline-flex items-center gap-1">
                <Spinner size="xs" />
                {event.attachments.length ? 'Uploading…' : 'Sending…'}
              </span>
            ) : (
              formatRelative(event.created_at)
            )}
            {event.edited_at ? ' · edited' : ''}
          </span>
          {pending?.status === 'failed' ? (
            <span className="flex items-center gap-2 font-medium text-destructive">
              Couldn’t send
              <button type="button" className="underline" onClick={pending.retry}>
                Retry
              </button>
              <button type="button" className="text-muted-foreground underline" onClick={() => dismissEvent(task.id, event.id)}>
                Discard
              </button>
            </span>
          ) : null}
          {mine && !editing ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-xs" className="-my-1 ml-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100" aria-label="Message actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {event.body ? (
                  <DropdownMenuItem onSelect={() => setEditing(markupToDisplay(event.body ?? ''))}>Edit</DropdownMenuItem>
                ) : null}
                <DropdownMenuItem variant="destructive" onSelect={() => void remove()}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        {editing ? (
          <div className="mt-1 space-y-2">
            <MentionTextarea
              aria-label="Edit message"
              tagging
              value={editing.text}
              onChange={(text) => setEditing((e) => (e ? { ...e, text } : e))}
              mentions={editing.mentions}
              onMentionsChange={(mentions) => setEditing((e) => (e ? { ...e, mentions } : e))}
              members={members}
              rows={2}
              autoFocus
              onSubmit={save}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!editing.text.trim()} onClick={save}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {event.body ? (
              <FactoryMessageBody body={event.body} round={round} members={members} contexts={task.contexts} onNavigate={onNavigate} />
            ) : null}
            <AttachmentList attachments={event.attachments} />
          </>
        )}
      </div>
    </div>
  );
}

const KIND_ICONS: Record<TaskEvent['kind'], LucideIcon> = {
  created: Plus,
  chat: MessageSquare,
  moved: ArrowRight,
  renamed: Pencil,
  described: Pencil,
  assigned: UserPlus,
  unassigned: UserMinus,
  context_added: Link2,
  context_removed: Unlink,
  label_added: Tag,
  label_removed: Tag,
  archived: Archive,
  unarchived: ArchiveRestore,
  snoozed: AlarmClock,
  unsnoozed: AlarmClockOff,
  repeat_set: Repeat,
  repeated: Repeat,
  type_set: Shapes,
  field_set: SlidersHorizontal,
  waiting_added: Hourglass,
  waiting_removed: CircleCheck,
};

/** "via Claude key": a member's API key made this (Team settings › API keys). */
function ViaApiKey({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground" data-hint="Made with this API key">
      <KeyRound className="size-3" />
      via {name}
    </span>
  );
}

function SystemLine({ event, members, labels }: { event: TaskEvent; members: TaskMember[]; labels: TaskLabel[] }) {
  const Icon = KIND_ICONS[event.kind] ?? Pencil;
  const actor = members.find((m) => m.user_id === event.actor_user_id);
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="z-10 flex size-6 shrink-0 items-center justify-center rounded-full border bg-background">
        <Icon className="size-3" />
      </span>
      <span className="min-w-0 flex-1">
        {/* A snooze ending by itself, a Repeat coming round or Jev's suggestion has no one to name. */}
        {!hasNoActor(event) ? (
          <>
            <span className="font-medium text-foreground/80" data-hint={memberName(actor)}>
              {memberHandle(actor)}
            </span>{' '}
          </>
        ) : null}
        {event.via_api_key_name ? (
          <>
            <ViaApiKey name={event.via_api_key_name} />{' '}
          </>
        ) : null}
        {systemLine(event, members, labels)}
      </span>
      <span className="shrink-0" data-hint={formatDateTime(new Date(event.created_at))}>
        {formatRelative(event.created_at)}
      </span>
    </div>
  );
}

/** Runs of system lines longer than this fold down to the last two, with a "show" button. */
const FOLD_AFTER = 3;

type Block = { kind: 'chat'; event: TaskEvent } | { kind: 'updates'; key: string; events: TaskEvent[] };

function toBlocks(events: TaskEvent[]): Block[] {
  const blocks: Block[] = [];
  for (const event of events) {
    const last = blocks[blocks.length - 1];
    if (event.kind === 'chat') blocks.push({ kind: 'chat', event });
    else if (last?.kind === 'updates') last.events.push(event);
    else blocks.push({ kind: 'updates', key: event.id, events: [event] });
  }
  return blocks;
}

/** Chat and history in one stream, newest at the bottom, with the composer under it. */
export function TaskTimeline({
  task,
  hydrated = true,
  showComposer = true,
  fill = false,
  members,
  labels,
  onNavigate,
}: {
  task: TaskDetail;
  /** False while only the card is known: the timeline shows a placeholder. */
  hydrated?: boolean;
  /** Phones pin the chat box to the bottom of the screen instead (see TaskDetailView). */
  showComposer?: boolean;
  /** Fills its pane (the pop-up's right side): the stream scrolls, the chat box stays at the foot. */
  fill?: boolean;
  members: TaskMember[];
  labels: TaskLabel[];
  onNavigate?: () => void;
}) {
  // "Show changes" is remembered per browser, for every task (on unless turned off).
  const [showActivity, setShowActivity] = usePref(UI_PREFS.tasksShowChanges);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const events = showActivity ? task.events : task.events.filter((e) => e.kind === 'chat');
  const blocks = toBlocks(events);
  // The factory's question and suggestion rounds (T-55): their comments point to the Decisions.
  const rounds = useMemo(() => roundsByMessage(task.events), [task.events]);

  // Filling a pane, it opens on the newest message and follows new ones.
  const stream = useRef<HTMLDivElement>(null);
  const eventCount = task.events.length;
  useLayoutEffect(() => {
    if (fill && stream.current) stream.current.scrollTop = stream.current.scrollHeight;
  }, [fill, eventCount, hydrated]);

  const header = (
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Activity</h3>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={showActivity}
            onCheckedChange={setShowActivity}
            aria-label="Show changes"
          />
          Show changes
        </label>
      </div>
  );

  const body = (
    <>
      {!hydrated && !blocks.length ? (
        <div className="space-y-3" aria-label="Loading activity">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="size-6 animate-pulse rounded-full bg-muted" />
              <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : blocks.length ? (
        <div className="relative space-y-3 before:absolute before:bottom-2 before:left-3 before:top-2 before:w-px before:bg-border">
          {blocks.map((block) => {
            if (block.kind === 'chat') {
              return <ChatMessage key={block.event.id} task={task} event={block.event} members={members} round={rounds.get(block.event.id)} onNavigate={onNavigate} />;
            }
            const folded = block.events.length > FOLD_AFTER && !expanded.has(block.key);
            const shown = folded ? block.events.slice(-2) : block.events;
            return (
              <div key={block.key} className="space-y-2">
                {folded ? (
                  <button
                    type="button"
                    className="relative z-10 ml-9 text-xs font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setExpanded((prev) => new Set(prev).add(block.key))}
                  >
                    Show {block.events.length - shown.length} earlier changes
                  </button>
                ) : null}
                {shown.map((event) => (
                  <SystemLine key={event.id} event={event} members={members} labels={labels} />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState size="compact" title="No messages yet" />
      )}
    </>
  );

  return (
    <section className={cn(fill ? 'flex h-full min-h-0 flex-col gap-3' : 'space-y-3')}>
      {header}
      {fill ? (
        <div ref={stream} className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 py-1">
          {body}
        </div>
      ) : (
        body
      )}
      {showComposer ? (
        <div className={cn(fill && 'shrink-0')}>
          <TaskChatComposer taskId={task.id} members={members} />
        </div>
      ) : null}
    </section>
  );
}
